import React, { useState, useEffect } from "react";
import { SUPERADMIN_USER_ID } from "constants/constants";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Icon,
  Select,
  Button,
  Badge,
  Checkbox,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Flex,
  SimpleGrid,
  Tooltip,
} from "@chakra-ui/react";
import { FaCheck } from "react-icons/fa";
import { SlackChannel, SlackNotificationConfig, SlackNotificationRequest, SlackUser } from "build-ts/slack_notification";
import { getSlackChannels, getSlackChannelUsers } from "services/AnalystService";
import { Header } from "build-ts/metadata";
import { ScheduledScenarioJobProto, ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";
import { MetadataTypeEnum } from "build-ts/enums";
import { v4 as uuidv4 } from "uuid";
import { useParams } from "react-router-dom";
import { putScheduledJob } from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import { getScheduledJob } from "services/ScheduleServices";
import CronBuilder from "./CronBuilder";
import lodash from "lodash";
import { MdEdit } from "react-icons/md";
import { MarkdownRenderer } from "components/agentChat/MessageComponent";
import { useUser } from "context/UserContext";
import WorkflowProcessSectionHeading from "components/analyst/WorkflowProcessSectionHeading";
export type JobFormView = "process" | "guidance";

interface JobCreationComponentProps {
  jobName: string;
  setJobName: (value: string) => void;
  selectedJob?: Header;
  isCreatingNew?: boolean;
  triggerSave?: boolean;
  setTriggerSave?: (value: boolean) => void;
  triggerDiscard?: boolean;
  setTriggerDiscard?: (value: boolean) => void;
  setIsSaving?: (value: boolean) => void;
  onSaveSuccess?: () => void;
  setHasFormChanges?: (hasChanges: boolean) => void;
  /** Which slice of the job form to show (single instance preserves state across tab switches). */
  jobFormView?: JobFormView;
}

const JobCreationComponent: React.FC<JobCreationComponentProps> = ({
  jobName,
  setJobName,
  selectedJob,
  isCreatingNew = false,
  triggerSave = false,
  setTriggerSave,
  triggerDiscard = false,
  setTriggerDiscard,
  setIsSaving,
  onSaveSuccess,
  setHasFormChanges,
  jobFormView = "process",
}) => {
  const { id: analystId } = useParams();
  const { userId } = useUser();
  // Analysis state
  const [trigger, setTrigger] = useState<string>("");
  const [analysisInstructions, setAnalysisInstructions] = useState<string>("");
  const [reportingInstructions, setReportingInstructions] = useState<string>("");
  const [historicalInstructions, setHistoricalInstructions] = useState<string>("");
  const [temporaryInstructions, setTemporaryInstructions] = useState<string>("");

  // Communication state
  const [slackTitle, setSlackTitle] = useState<string>("");
  const [slackChannels, setSlackChannels] = useState<SlackChannel[]>([]);
  const [selectedSlackChannel, setSelectedSlackChannel] = useState<SlackChannel | null>(null);
  const [channelUsers, setChannelUsers] = useState<SlackUser[]>([]);
  const [selectedSlackUsers, setSelectedSlackUsers] = useState<SlackUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
  const [lastFetchedChannelId, setLastFetchedChannelId] = useState<string | null>(null);

  // Collapsible sections state;
  const [selectedJobDetails, setSelectedJobDetails] = useState<ScheduledJobProto | undefined>(undefined);

  // Schedule state
  const [isActive, setIsActive] = useState<boolean>(false);
  const [cronExpression, setCronExpression] = useState<string>("");

  const [isReportingInstructionsEditMode, setIsReportingInstructionsEditMode] = useState(false);
  const [isGuidanceRawEditMode, setIsGuidanceRawEditMode] = useState(false);

  useEffect(() => {
    setIsReportingInstructionsEditMode(false);
    setIsGuidanceRawEditMode(false);
  }, [selectedJob?.id, jobFormView]);

  // Original data state for tracking changes
  const [originalData, setOriginalData] = useState<{
    jobName: string;
    trigger: string;
    analysisInstructions: string;
    reportingInstructions: string;
    historicalInstructions: string;
    temporaryInstructions: string;
    slackTitle: string;
    selectedSlackChannel: SlackChannel | null;
    selectedSlackUsers: SlackUser[];
    cronExpression: string;
  }>({
    jobName: "",
    trigger: "",
    analysisInstructions: "",
    reportingInstructions: "",
    historicalInstructions: "",
    temporaryInstructions: "",
    slackTitle: "",
    selectedSlackChannel: null,
    selectedSlackUsers: [],
    cronExpression: "",
  });

  // Get current form data
  const getCurrentFormData = () => {
    return {
      jobName,
      trigger,
      analysisInstructions,
      reportingInstructions,
      historicalInstructions,
      temporaryInstructions,
      slackTitle,
      selectedSlackChannel,
      selectedSlackUsers,
      cronExpression,
    };
  };

  // Check if form has changes
  useEffect(() => {
    const currentFormData = getCurrentFormData();
    const isChanged = !lodash.isEqual(originalData, currentFormData);
    setHasFormChanges?.(isChanged);
  }, [
    jobName,
    trigger,
    analysisInstructions,
    reportingInstructions,
    historicalInstructions,
    temporaryInstructions,
    slackTitle,
    selectedSlackChannel,
    selectedSlackUsers,
    cronExpression,
    isActive,
  ]);

  const fetchJobDetails = async (selectedJobId: string) => {
    const response = await getScheduledJob(selectedJobId, false);
    if (response) {
      setSelectedJobDetails(response);

      const jobNameValue = response.header?.name || "";
      const triggerValue = response.scheduled_analysis?.trigger || "";
      const analysisInstructionsValue = response.scheduled_analysis?.analysis_instructions || "";
      const reportingInstructionsValue = response.scheduled_analysis?.reporting_instructions || "";
      const historicalInstructionsValue = response.scheduled_analysis?.historical_instructions || "";
      const temporaryInstructionsValue = response.scheduled_analysis?.temporary_instructions || "";
      const slackTitleValue = response.scheduled_analysis?.slack_notification_request?.notification_config?.message_title || "";
      const slackChannelValue = response.scheduled_analysis?.slack_notification_request?.notification_config?.channel || null;
      const slackUsersValue = response.scheduled_analysis?.slack_notification_request?.notification_config?.tag_users || [];
      const cronExprValue = response.cron_expr || "";
      const isActiveValue = response.is_active || false;

      setJobName(jobNameValue);
      setTrigger(triggerValue);
      setAnalysisInstructions(analysisInstructionsValue);
      setReportingInstructions(reportingInstructionsValue);
      setHistoricalInstructions(historicalInstructionsValue);
      setTemporaryInstructions(temporaryInstructionsValue);
      setSlackTitle(slackTitleValue);
      setSelectedSlackChannel(slackChannelValue);
      setSelectedSlackUsers(slackUsersValue);
      setCronExpression(cronExprValue);
      setIsActive(isActiveValue);

      setOriginalData({
        jobName: jobNameValue,
        trigger: triggerValue,
        analysisInstructions: analysisInstructionsValue,
        reportingInstructions: reportingInstructionsValue,
        historicalInstructions: historicalInstructionsValue,
        temporaryInstructions: temporaryInstructionsValue,
        slackTitle: slackTitleValue,
        selectedSlackChannel: slackChannelValue,
        selectedSlackUsers: slackUsersValue,
        cronExpression: cronExprValue,
      });
    }
  };

  const handleSaveJob = async () => {
    if (!jobName.trim()) {
      showToast("Please enter a job name", "", "warning");
      return;
    }

    setIsSaving?.(true);

    const job = ScheduledJobProto.create({
      header: Header.create({
        id: selectedJob?.id || uuidv4(),
        name: jobName.trim(),
        type: MetadataTypeEnum.SCHEDULED_JOB,
        owner_id: analystId,
      }),
      type: ScheduledJobType.SCENARIO_JOB,
      cron_expr: cronExpression,
      is_active: isActive,
      scheduled_analysis: ScheduledScenarioJobProto.create({
        trigger: trigger.trim(),
        darwin_name: selectedJobDetails?.scheduled_analysis?.darwin_name,
        analysis_instructions: analysisInstructions.trim(),
        reporting_instructions: reportingInstructions.trim(),
        historical_instructions: historicalInstructions.trim(),
        temporary_instructions: temporaryInstructions.trim(),
        slack_notification_request: SlackNotificationRequest.create({
          notification_config: SlackNotificationConfig.create({
            channel: selectedSlackChannel
              ? SlackChannel.create({
                id: selectedSlackChannel.id,
                name: selectedSlackChannel.name,
                is_private: selectedSlackChannel.is_private,
                num_members: selectedSlackChannel.num_members,
              })
              : undefined,
            tag_users: selectedSlackUsers,
            message_title: slackTitle.trim(),
          }),
        }),
      }),
    });

    const response = await putScheduledJob(job);
    setIsSaving?.(false);

    if (response) {
      showToast(
        isCreatingNew ? "Job created successfully" : "Job updated successfully",
        "",
        "success"
      );
      onSaveSuccess?.();
    } else {
      showToast(
        isCreatingNew ? "Failed to create job" : "Failed to update job",
        "",
        "error"
      );
    }
  };

  // Watch for triggerSave changes to trigger save
  useEffect(() => {
    if (triggerSave) {
      handleSaveJob();
      setTriggerSave?.(false);
    }
  }, [triggerSave]);

  // Watch for triggerDiscard to reset all fields back to their last-saved values
  useEffect(() => {
    if (triggerDiscard) {
      setJobName(originalData.jobName);
      setTrigger(originalData.trigger);
      setAnalysisInstructions(originalData.analysisInstructions);
      setReportingInstructions(originalData.reportingInstructions);
      setHistoricalInstructions(originalData.historicalInstructions);
      setTemporaryInstructions(originalData.temporaryInstructions);
      setSlackTitle(originalData.slackTitle);
      setSelectedSlackChannel(originalData.selectedSlackChannel);
      setSelectedSlackUsers(originalData.selectedSlackUsers);
      setCronExpression(originalData.cronExpression);
      setTriggerDiscard?.(false);
    }
  }, [triggerDiscard]);

  // Fetch slack channels on component mount
  useEffect(() => {
    const fetchSlackChannels = async () => {
      const channels = await getSlackChannels();
      if (channels) {
        setSlackChannels(channels);
      }
    };
    fetchSlackChannels();
  }, []);

  // Fetch job details when selectedJob changes
  useEffect(() => {
    if (selectedJob?.id) {
      fetchJobDetails(selectedJob.id);
    } else {
      setJobName("");
      setTrigger("");
      setAnalysisInstructions("");
      setReportingInstructions("");
      setHistoricalInstructions("");
      setTemporaryInstructions("");
      setSlackTitle("");
      setSelectedSlackChannel(null);
      setSelectedSlackUsers([]);
      setIsLoadingUsers(false);
      setLastFetchedChannelId(null);
      setCronExpression("");
      setIsActive(false);
      setSelectedJobDetails(undefined);

      setOriginalData({
        jobName: "",
        trigger: "",
        analysisInstructions: "",
        reportingInstructions: "",
        historicalInstructions: "",
        temporaryInstructions: "",
        slackTitle: "",
        selectedSlackChannel: null,
        selectedSlackUsers: [],
        cronExpression: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob?.id]);

  // Fetch slack channel users when channel is selected
  useEffect(() => {
    const fetchChannelUsers = async () => {
      if (selectedSlackChannel?.id) {
        if (selectedSlackChannel.id === lastFetchedChannelId) {
          return;
        }

        setIsLoadingUsers(true);
        try {
          const users = await getSlackChannelUsers(selectedSlackChannel.id);
          if (users) {
            setChannelUsers(users);
          } else {
            setChannelUsers([]);
          }
          setLastFetchedChannelId(selectedSlackChannel.id);
        } catch (error) {
          console.error("Error fetching channel users:", error);
          setChannelUsers([]);
        } finally {
          setIsLoadingUsers(false);
        }
      } else {
        setChannelUsers([]);
        setLastFetchedChannelId(null);
        setSelectedSlackUsers([]);
        setIsLoadingUsers(false);
      }
    };
    fetchChannelUsers();
  }, [selectedSlackChannel]);

  // Handle Slack channel selection
  const handleSlackChannelChange = (channelId: string) => {
    if (channelId === "") {
      setSelectedSlackChannel(null);
    } else {
      const channel = slackChannels.find((c) => c.id === channelId);
      setSelectedSlackChannel(channel || null);
    }
    setSelectedSlackUsers([]);
  };

  // Remove selected slack user
  const removeSlackUser = (userId: string) => {
    setSelectedSlackUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  /** Left: objective, schedule, temp, Slack. Right: instructions (markdown) — shared for chrome and jobs list. */
  const processFormCore = (
    <Flex
      direction={{ base: "column", lg: "row" }}
      align="stretch"
      gap={6}
      w="100%"
      flex="1"
      minH={0}
    >
      <VStack
        spacing={5}
        align="stretch"
        w={{ base: "100%", lg: "auto" }}
        minW={{ lg: "260px" }}
        maxW={{ lg: "400px" }}
        flexShrink={0}
      >
        <FormControl display="flex" flexDirection="column" minW={0}>
          <WorkflowProcessSectionHeading
            label="Objective"
            tooltip="When should this analysis fire? Define the metric thresholds and data conditions."
          />
          <Textarea
            value={trigger}
            onChange={(e) => setTrigger(e.target.value)}
            placeholder="e.g. CPL_today > 100 AND CPL_3day_avg < 50, spend > 0…"
            rows={7}
            resize="none"
            minH="4.5rem"
            _focus={{
              borderColor: "purple.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
            }}
            fontWeight="500"
            fontSize="sm"
          />
        </FormControl>

        <Box display="flex" flexDirection="column" minW={0} w="100%">
          <WorkflowProcessSectionHeading label="Schedule" />
          <Box borderRadius="md" minH={0}>
            <CronBuilder
              cronExpression={cronExpression}
              setCronExpression={setCronExpression}
              isActive={isActive}
              setIsActive={setIsActive}
              jobDetails={selectedJobDetails}
            />
          </Box>
        </Box>

        <FormControl>
          <WorkflowProcessSectionHeading label="Temporary instructions" />
          <Textarea
            size="md"
            value={temporaryInstructions}
            onChange={(e) => setTemporaryInstructions(e.target.value)}
            placeholder="One-off instructions that override defaults for a single run…"
            fontSize="sm"
            rows={4}
            resize="none"
            _focus={{
              borderColor: "purple.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
            }}
          />
        </FormControl>
        {userId === SUPERADMIN_USER_ID && (
          <FormControl>

            <WorkflowProcessSectionHeading label="Communication" />



            <VStack spacing={4} align="stretch" p={3}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm" mb={1} fontWeight="600">
                    Notification title
                  </FormLabel>
                  <Input
                    size="sm"
                    borderRadius="md"
                    value={slackTitle}
                    onChange={(e) => setSlackTitle(e.target.value)}
                    placeholder="Enter notification title"
                    _focus={{
                      borderColor: "purple.700",
                      boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)",
                    }}
                    fontWeight="500"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={1} fontWeight="600">
                    Slack channel
                  </FormLabel>
                  <Select
                    size="sm"
                    borderRadius="md"
                    value={selectedSlackChannel?.id || ""}
                    onChange={(e) => handleSlackChannelChange(e.target.value)}
                    placeholder="Choose a channel"
                  >
                    {slackChannels.map((channel) => (
                      <option key={channel.id} value={channel.id}>
                        {channel.name}
                      </option>
                    ))}
                  </Select>
                  {selectedSlackChannel && (
                    <Flex mt={2}>
                      <Badge colorScheme="blue" fontSize="xs">
                        {selectedSlackChannel.name}
                      </Badge>
                    </Flex>
                  )}
                </FormControl>
              </SimpleGrid>
              <FormControl>
                <FormLabel fontSize="sm" mb={1} fontWeight="600">
                  Notify users
                </FormLabel>
                <Popover placement="bottom-start" strategy="fixed">
                  <PopoverTrigger>
                    <Button
                      size="sm"
                      variant="outline"
                      isDisabled={!selectedSlackChannel || isLoadingUsers}
                      isLoading={isLoadingUsers}
                      loadingText="Loading users…"
                      bg={!selectedSlackChannel || isLoadingUsers ? "gray.100" : "white"}
                      textAlign="left"
                      fontWeight="normal"
                      width="100%"
                      justifyContent="flex-start"
                      _hover={!selectedSlackChannel || isLoadingUsers ? {} : { bg: "gray.50" }}
                    >
                      {isLoadingUsers
                        ? "Loading users…"
                        : selectedSlackUsers.length === 0
                          ? selectedSlackChannel
                            ? "Choose users"
                            : "Select a channel first"
                          : selectedSlackUsers.length === 1
                            ? selectedSlackUsers[0].display_name || selectedSlackUsers[0].name
                            : `${selectedSlackUsers.length} users selected`}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent width="300px" zIndex="popover">
                    <PopoverBody maxHeight="200px" overflowY="auto" p={3}>
                      <VStack spacing={2} align="stretch">
                        {isLoadingUsers ? (
                          <Text fontSize="sm" color="gray.500" textAlign="center">
                            Loading users…
                          </Text>
                        ) : channelUsers.length === 0 ? (
                          <Text fontSize="sm" color="gray.500" textAlign="center">
                            No users available
                          </Text>
                        ) : (
                          channelUsers.map((user) => (
                            <HStack key={user.id} spacing={3} w="100%" align="center">
                              <Checkbox
                                isChecked={selectedSlackUsers.some((u) => u.id === user.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    if (!selectedSlackUsers.some((u) => u.id === user.id)) {
                                      setSelectedSlackUsers((prev) => [...prev, user]);
                                    }
                                  } else {
                                    setSelectedSlackUsers((prev) =>
                                      prev.filter((u) => u.id !== user.id)
                                    );
                                  }
                                }}
                                size="sm"
                                colorScheme="purple"
                              />
                              <Text fontSize="sm" color="gray.700" ml={1} mt={1}>
                                {user.display_name || user.name}
                              </Text>
                            </HStack>
                          ))
                        )}
                      </VStack>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
                {selectedSlackUsers.length > 0 && (
                  <Flex mt={2} flexWrap="wrap" gap={2}>
                    {selectedSlackUsers.map((user) => (
                      <Badge
                        key={user.id}
                        colorScheme="purple"
                        fontSize="xs"
                        cursor="pointer"
                        onClick={() => removeSlackUser(user.id!)}
                        _hover={{ bg: "purple.200" }}
                        textTransform="none"
                      >
                        {user.display_name || user.name} ✕
                      </Badge>
                    ))}
                  </Flex>
                )}
              </FormControl>
            </VStack>
          </FormControl>
        )}
      </VStack >

      <Flex
        direction="column"
        flex="1"
        minW={0}
        minH={{ base: "min(55vh, 440px)", lg: "280px" }}
        align="stretch"
      >
        <FormControl display="flex" flexDirection="column" flex="1" minH={0} h={{ lg: "100%" }}>
          <WorkflowProcessSectionHeading
            label="Instructions"
            tooltip="Same rendering as chat messages (markdown, GFM, math). Use Edit for raw source."
            rightSlot={
              isReportingInstructionsEditMode ? (
                <Button
                  size="xs"
                  colorScheme="purple"
                  variant="solid"
                  leftIcon={<Icon as={FaCheck} boxSize={3.5} />}
                  onClick={() => setIsReportingInstructionsEditMode(false)}
                  fontSize="xs"
                >
                  Done
                </Button>
              ) : (
                <Button
                  size="2xs"
                  variant="ghost"
                  colorScheme="purple"
                  leftIcon={<Icon as={MdEdit} boxSize={4} />}
                  onClick={() => setIsReportingInstructionsEditMode(true)}
                  fontSize="xs"
                >
                  Edit
                </Button>
              )
            }
          />
          {isReportingInstructionsEditMode ? (
            <Textarea
              value={reportingInstructions}
              onChange={(e) => setReportingInstructions(e.target.value)}
              placeholder="Write markdown: headings, lists, tables (GFM), code fences…"
              flex="1"
              minH={{ base: "260px", lg: "320px" }}
              resize="vertical"
              w="100%"
              fontWeight="500"
              fontSize="sm"
              fontFamily="mono"
              _focus={{
                borderColor: "purple.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
              }}
            />
          ) : (
            <Tooltip
              label="Locked. Click Edit to modify."
              placement="top"
              hasArrow
            >
              <Box
                flex="1"
                minH={0}
                w="100%"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                px={4}
                py={0}
                bg="white"
                overflowY="auto"
                overflowX="auto"
                cursor="not-allowed"
              >
                {reportingInstructions.trim() ? (
                  <MarkdownRenderer
                    content={reportingInstructions}
                    isArtifactsPanelVisible={true}
                  />
                ) : (
                  <Text fontSize="sm" color="gray.500" mb={0}>
                    No instructions yet. Click Edit to add markdown.
                  </Text>
                )}
              </Box>
            </Tooltip>
          )}
        </FormControl>
      </Flex>
    </Flex >
  );

  if (jobFormView === "guidance") {
    return (
      <Flex direction="column" flex="1" minH={0} h="100%" overflow="hidden" px={2} bg="white">
        <Flex direction="column" flex="1" minH={0} px={9} py={2} w="100%" maxW="100%" overflow="hidden">
          <Box flexShrink={0} position="sticky" top={0} zIndex={1} bg="white" pb={2}>
            <WorkflowProcessSectionHeading
              label="For Internal Use Only"
              tooltip="Internal guidance for the agent to perform workflow tasks more reliably."
              rightSlot={
                isGuidanceRawEditMode ? (
                  <Button
                    size="sm"
                    colorScheme="purple"
                    variant="solid"
                    leftIcon={<Icon as={FaCheck} boxSize={3.5} />}
                    onClick={() => setIsGuidanceRawEditMode(false)}
                  >
                    Done
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="purple"
                    leftIcon={<Icon as={MdEdit} boxSize={4} />}
                    onClick={() => setIsGuidanceRawEditMode(true)}
                  >
                    Edit
                  </Button>
                )
              }
            />
          </Box>
          {isGuidanceRawEditMode ? (
            <Textarea
              flex="1"
              minH={0}
              value={analysisInstructions}
              onChange={(e) => setAnalysisInstructions(e.target.value)}
              placeholder={"# analysis instructions\n# how to build the data query…"}
              w="100%"
              minW={0}
              resize="none"
              overflowY="auto"
              fontWeight="500"
              fontSize="sm"
              fontFamily="mono"
              _focus={{
                borderColor: "purple.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
              }}
            />
          ) : (
            <Box
              flex="1"
              minH={0}
              w="100%"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              px={4}
              py={3}
              bg="gray.50"
              overflowY="auto"
              overflowX="auto"
            >
              {analysisInstructions.trim() ? (
                <MarkdownRenderer content={analysisInstructions} isArtifactsPanelVisible={true} />
              ) : (
                <Text fontSize="sm" color="gray.500" mb={0}>
                  No guidance yet. Click Edit to add markdown or plain text.
                </Text>
              )}
            </Box>
          )}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex direction="column" flex="1" minH={0} h="100%" overflow="hidden" px={2}>
      <Flex direction="column" flex="1" minH={0} px={9} py={4} w="100%" maxW="100%" overflow="hidden">
        {processFormCore}
      </Flex>
    </Flex>
  );
};

export default JobCreationComponent;
